import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cardSliderMetrics } from '../constants/cardSliderWidget';
import { VIDEO_SLIDER_CSS, videoEmbedSrc, videoFrameAspect, videoSourceInfo } from '../constants/videoCardSliderWidget';

function stop(event) {
  event.preventDefault();
  event.stopPropagation();
}

/**
 * Palco do carrossel de vídeos. A fileira usa a mesma conta do carrossel de cards.
 */
export default function RenderVideoCardSlider({ content = {} }) {
  const viewportRef = useRef(null);
  const [box, setBox] = useState({ width: 0, mobile: false });
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  const [modalSrc, setModalSrc] = useState('');
  const [inlineIndex, setInlineIndex] = useState(-1);
  const videos = Array.isArray(content.videos) ? content.videos.slice(0, 12) : [];
  const metrics = cardSliderMetrics({ ...content, cards: videos }, box.width, page, box.mobile);
  const layout = content.layout === 'youtube' ? 'youtube' : 'netflix';
  const inline = content.openMode === 'inline';
  const playing = Boolean(modalSrc) || inlineIndex >= 0;
  const aspect = videoFrameAspect(content.aspect);
  const fixedRatio = aspect === 0;
  const frameHeight = fixedRatio
    ? Math.max(80, Math.min(420, parseInt(content.imageHeight, 10) || 180))
    : (metrics.cardW ? metrics.cardW / aspect : 0);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const canvas = viewport?.closest('.course-wrapper');
    const measure = () => {
      setBox({
        width: viewport ? viewport.clientWidth : 0,
        mobile: !!canvas?.classList.contains('geppetto-edit-bp-mobile'),
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (viewport) observer.observe(viewport);
    const classObserver = canvas ? new MutationObserver(measure) : null;
    classObserver?.observe(canvas, { attributes: true, attributeFilter: ['class'] });
    return () => {
      observer.disconnect();
      classObserver?.disconnect();
    };
  }, [videos.length, layout, content.perPage, content.perPageMobile]);

  useEffect(() => {
    if (!content.autoplay || paused || playing || metrics.starts.length < 2) return undefined;
    const ms = Math.max(1200, parseInt(content.autoplayMs, 10) || 5000);
    const timer = setInterval(() => {
      setPage((current) => {
        if (content.loop === false && current >= metrics.starts.length - 1) return current;
        return current + 1;
      });
    }, ms);
    return () => clearInterval(timer);
  }, [content.autoplay, content.autoplayMs, content.loop, metrics.starts.length, paused, playing]);

  useEffect(() => {
    if (!modalSrc) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [modalSrc]);

  const go = (delta) => {
    const total = metrics.starts.length;
    if (total < 2) return;
    setPage((current) => {
      const next = current + delta;
      if (content.loop === false) return Math.max(0, Math.min(total - 1, next));
      return next;
    });
  };

  const openVideo = (index, src) => {
    if (!src) return;
    if (inline) {
      setInlineIndex(index);
      setModalSrc('');
      return;
    }
    setInlineIndex(-1);
    setModalSrc(videoEmbedSrc(videos[index]?.videoUrl, true));
  };

  const vars = {
    '--vs-card': content.cardBg || '#ffffff',
    '--vs-title': content.titleColor || '#0f172a',
    '--vs-play': content.playBg || '#ffffff',
    '--vs-play-text': content.playColor || '#0f172a',
    '--vs-arrow': content.arrowBg || '#ffffff',
    '--vs-arrow-text': content.arrowColor || '#0f172a',
    '--vs-dot': content.dotColor || '#cbd5e1',
    '--vs-dot-on': content.dotActive || '#2563eb',
    '--vs-card-radius': `${Math.max(0, Math.min(40, parseInt(content.cardRadius, 10) || 14))}px`,
    '--vs-video-radius': `${Math.max(0, Math.min(40, parseInt(content.videoRadius, 10) || 12))}px`,
    '--vs-img': `${Math.max(80, Math.min(420, parseInt(content.imageHeight, 10) || 180))}px`,
    '--vs-aspect': String(fixedRatio ? 1.7778 : aspect),
    '--vs-arrow-y': frameHeight ? `${frameHeight / 2}px` : '30%',
  };
  const classes = [
    'madri-video-slider',
    layout === 'youtube' ? 'madri-video-slider--youtube' : 'madri-video-slider--netflix',
    fixedRatio ? 'madri-video-slider--ratio-fixed' : '',
    content.shadow === false ? '' : 'madri-video-slider--shadow',
    content.hoverLift === false ? '' : 'madri-video-slider--lift',
  ].filter(Boolean).join(' ');

  return (
    <div className={classes} style={vars} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <style>{VIDEO_SLIDER_CSS}</style>
      <div
        className="madri-video-slider__viewport"
        ref={viewportRef}
        onPointerUp={(event) => {
          const start = event.currentTarget.dataset.x;
          if (start == null || event.target.closest('a,button')) return;
          const dx = event.clientX - Number(start);
          if (dx > 48) go(-1);
          else if (dx < -48) go(1);
        }}
        onPointerDown={(event) => {
          if (event.target.closest('a,button')) return;
          event.currentTarget.dataset.x = String(event.clientX);
        }}
      >
        <div
          className="madri-video-slider__track"
          style={{ gap: metrics.gap, transform: `translateX(${box.width ? metrics.x : 0}px)` }}
        >
          {videos.map((video, index) => (
            <VideoCard
              key={`${video.title || 'video'}-${index}`}
              video={video}
              width={box.width ? metrics.cardW : undefined}
              inlineSrc={inline && inlineIndex === index ? videoEmbedSrc(video.videoUrl, true) : ''}
              onPlay={(event) => {
                stop(event);
                openVideo(index, videoEmbedSrc(video.videoUrl, false));
              }}
            />
          ))}
        </div>
      </div>
      {content.showArrows !== false && metrics.starts.length > 1 && (
        <>
          <button type="button" className="madri-video-slider__arrow madri-video-slider__arrow--prev" aria-label="Anterior" disabled={content.loop === false && metrics.page <= 0} onClick={(event) => { stop(event); go(-1); }}>‹</button>
          <button type="button" className="madri-video-slider__arrow madri-video-slider__arrow--next" aria-label="Próximo" disabled={content.loop === false && metrics.page >= metrics.starts.length - 1} onClick={(event) => { stop(event); go(1); }}>›</button>
        </>
      )}
      {content.showDots !== false && metrics.starts.length > 1 && (
        <div className="madri-video-slider__dots">
          {metrics.starts.map((start, index) => (
            <button
              key={start}
              type="button"
              className={`madri-video-slider__dot${index === metrics.page ? ' is-active' : ''}`}
              aria-label={String(index + 1)}
              onClick={(event) => { stop(event); setPage(index); }}
            />
          ))}
        </div>
      )}
      {modalSrc && createPortal(
        <div className="madri-video-slider__modal" onClick={(event) => { if (event.target === event.currentTarget) { stop(event); setModalSrc(''); } }}>
          <div className="madri-video-slider__dialog" role="dialog">
            <button type="button" className="madri-video-slider__close" aria-label="Fechar" onClick={(event) => { stop(event); setModalSrc(''); }}>×</button>
            <div className="madri-video-slider__stage">
              <iframe src={modalSrc} title="" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

function VideoCard({ video, width, inlineSrc, onPlay }) {
  const title = String(video?.title || '').trim();
  const canPlay = Boolean(videoEmbedSrc(video?.videoUrl, false));
  const style = width ? { flex: `0 0 ${width}px`, width } : undefined;
  const glyph = <span className="madri-video-slider__glyph">&#9654;</span>;
  return (
    <div className="madri-video-slider__card" style={style}>
      <div className="madri-video-slider__frame">
        {inlineSrc ? (
          <iframe src={inlineSrc} title={title} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
        ) : (
          <>
            <OriginCover video={video} title={title} />
            {canPlay ? (
              <button type="button" className="madri-video-slider__play" aria-label={title || 'Play'} onClick={onPlay}>{glyph}</button>
            ) : (
              <span className="madri-video-slider__play" aria-hidden="true">{glyph}</span>
            )}
          </>
        )}
      </div>
      {title ? <div className="madri-video-slider__title">{title}</div> : null}
    </div>
  );
}

function OriginCover({ video, title }) {
  const custom = String(video?.coverUrl || '').trim();
  const info = videoSourceInfo(video?.videoUrl);
  const [vimeoThumb, setVimeoThumb] = useState('');
  const [youtubeSrc, setYoutubeSrc] = useState('');

  useEffect(() => {
    if (custom || info?.provider !== 'youtube') return undefined;
    setYoutubeSrc(`https://i.ytimg.com/vi/${info.id}/maxresdefault.jpg`);
    return undefined;
  }, [custom, info?.provider, info?.id]);

  useEffect(() => {
    if (custom || info?.provider !== 'vimeo' || !info.id) return undefined;
    let cancel = false;
    fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(`https://vimeo.com/${info.id}`)}`)
      .then((response) => response.json())
      .then((data) => {
        if (!cancel && data?.thumbnail_url) setVimeoThumb(String(data.thumbnail_url).replace(/_[0-9]+x[0-9]+/, '_640'));
      })
      .catch(() => {});
    return () => {
      cancel = true;
    };
  }, [custom, info?.provider, info?.id]);

  if (custom) return <img className="madri-video-slider__cover" src={custom} alt={title} />;
  if (info?.provider === 'youtube' && youtubeSrc) {
    return (
      <img
        className="madri-video-slider__cover"
        src={youtubeSrc}
        alt={title}
        onError={() => {
          const fallback = `https://i.ytimg.com/vi/${info.id}/hqdefault.jpg`;
          setYoutubeSrc((current) => (current === fallback ? current : fallback));
        }}
        onLoad={(event) => {
          if (event.currentTarget.naturalWidth > 200) return;
          const fallback = `https://i.ytimg.com/vi/${info.id}/hqdefault.jpg`;
          setYoutubeSrc((current) => (current === fallback ? current : fallback));
        }}
      />
    );
  }
  if (info?.provider === 'vimeo' && vimeoThumb) return <img className="madri-video-slider__cover" src={vimeoThumb} alt={title} />;
  if (info?.provider === 'panda') {
    const src = videoEmbedSrc(video?.videoUrl, false);
    if (src) return <iframe className="madri-video-slider__poster" src={src} title="" tabIndex={-1} />;
  }
  return null;
}
